import { cn } from "@universe/ui/lib/utils";

import {
  LEDGER_ROW_BOX_CLASSES,
  LEDGER_ROW_DIVIDER_CLASSES,
  LEDGER_ROW_LAYOUT_CLASSES,
  LEDGER_ROW_SEPARATOR_CLASSES,
  LEDGER_ROW_SHELL_CLASSES,
} from "@/components/ledger/ledger-row-shell";
import { LEDGER_ENTRY_DATE_TIME_SHORT_FORMAT } from "@/constants";
import { safeDateFormat } from "@/utils/safeDateFormat";

import FormattedCurrencyAmount from "../formatted-currency-amount";

import LedgerEntryListBasicInfo from "./ledger-entry-list-basic-info";

interface LedgerEntryItemProps {
  index: number;
  entry: LedgerEntryResolvedType;
  setSelectedEntry: () => void;
  selectedIndex: string | null;
}

export default function LedgerEntryItem({
  index,
  entry,
  setSelectedEntry,
  selectedIndex,
}: LedgerEntryItemProps) {
  if (!entry) return null;

  // Format date and time — resolvedData shape varies by journal type:
  // - payments/bank-transactions: { meta: { createTime }, valueDate, ... }
  // - fx-tickets (wrapped): { data: [{ meta: { createTime }, ... }] }
  // - fx-tickets (flattened): { meta: { createTime }, baseCurrency, ... }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rd = entry.resolvedData as any;
  const transactionDate = rd
    ? safeDateFormat(
        rd?.meta?.createTime ||
          rd?.data?.[0]?.meta?.createTime ||
          rd?.valueDate ||
          entry.valueDate,
        LEDGER_ENTRY_DATE_TIME_SHORT_FORMAT
      )
    : safeDateFormat(entry.valueDate, LEDGER_ENTRY_DATE_TIME_SHORT_FORMAT);

  const isSelected = selectedIndex === `${entry.uuid}-${index}`;

  return (
    <div
      className={`transition-colors hover:bg-gradient-to-l hover:from-white hover:to-brand-primary/5 hover:shadow-md transition-all ${LEDGER_ROW_SHELL_CLASSES}`}
    >
      {/* The row opens the entry drawer, so it is a button: Tab reaches it,
          Enter and Space open it, and its visible text (title, amount, date)
          is its name. A button does not stretch to its container as a block
          div does, so its width is set to the row plus the 24px the box's
          negative margins add. */}
      <button
        type="button"
        onClick={setSelectedEntry}
        aria-haspopup="dialog"
        aria-expanded={isSelected}
        className={cn(
          LEDGER_ROW_BOX_CLASSES,
          "block w-[calc(100%+24px)] text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-boundary",
          isSelected && "bg-brand-primary/10 shadow-md"
        )}
      >
        {/* Below md the row wraps: the info side takes the full width, so a
            long entry title keeps it, and the amount and date move onto their
            own line. A capped title beside the amount kept only 7 characters
            of a 40-character name at 320px, and 16 at 390px. No viewport cap
            holds the title at any width: at 1024px one cut a 40-character
            name to 36 with 219px of the row free. From md up the info side
            fills the row, and its `contain:inline-size` keeps the nowrap title
            out of the row's intrinsic width, which the cap used to do: without
            it a long title widens the sidebar inset past the screen from lg.
            Below md the title column carries the containment
            (`ledger-entry-list-basic-info.tsx`). */}
        <div className={cn(LEDGER_ROW_LAYOUT_CLASSES, "items-start")}>
          <LedgerEntryListBasicInfo
            className="min-w-0 basis-full md:flex-1 md:[contain:inline-size]"
            entry={entry}
          />

          <div className="text-right flex items-center gap-10 ml-auto">
            <div className="text-right flex flex-row items-baseline gap-3 md:flex-col md:items-end md:gap-1 xl:flex-row">
              <FormattedCurrencyAmount
                balance={entry.balance ?? 0}
                product={entry.product}
              />

              <div className={LEDGER_ROW_SEPARATOR_CLASSES} />

              <p className="text-sm xl:text-base text-gray-700 w-[80px]">
                {transactionDate}
              </p>
            </div>
          </div>
        </div>
      </button>
      <div className={LEDGER_ROW_DIVIDER_CLASSES} />
    </div>
  );
}
